import { inject, Injectable } from '@angular/core';
import { type Params } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { type SitePage, type SitePageEntity, type SitePageListEntity } from '@common/types/site-page.type';
import { getUuid, isUuid } from '@common/helpers';
import { EContentSources, EPageStatuses } from '@common/enums';
import { URLSafeParse } from '@common/helpers/url';

@Injectable({
    providedIn: 'root',
})
export class PagesService {
    private readonly translateService: TranslateService = inject(TranslateService);

    public isPageNew(page: SitePageEntity): boolean {
        if (!page?.status) {
            return false;
        }

        return page.status === EPageStatuses.New;
    }

    public isPageNotActive(page: SitePageEntity): boolean {
        if (!page?.status) {
            return false;
        }

        return page.status === EPageStatuses.NotActive;
    }

    public isPageActive(page: SitePageEntity): boolean {
        if (!page?.status) {
            return false;
        }

        return page.status === EPageStatuses.Active;
    }

    public isWebEducationPage(page: SitePageEntity): boolean {
        return page.content_source_id === EContentSources.WebEducation;
    }

    public isWebChatBotPage(page: SitePageEntity): boolean {
        return page.content_source_id === EContentSources.WebChatBot;
    }

    public isEducationPage(page: SitePageEntity): boolean {
        return page.content_source_id === EContentSources.Education;
    }

    public isEcommercePage(page: SitePageEntity): boolean {
        const parsed = URLSafeParse(page.url);
        return (typeof parsed === 'boolean' ? false : parsed.pathname) === '/internal/product';
    }

    public shouldHidePageInConstructor(page: SitePageEntity | SitePageListEntity): boolean {
        const hasEcommerceUrl =
            'url' in page && typeof page.url === 'string' ? this.isEcommercePage(page as SitePageEntity) : false;
        const hasEducationSource = this.isEducationPage(page as SitePageEntity);
        return hasEducationSource || hasEcommerceUrl;
    }

    public isPageNotSaved(page: SitePageEntity): boolean {
        return !page.updated_at;
    }

    public createEmptyPage(): Partial<SitePageEntity> {
        return {
            id: getUuid(),
            name: this.translateService.instant('new_page'),
            status: EPageStatuses.New,
        };
    }

    public findCurrentPage(pages: SitePageEntity[], routerParams: Params): SitePageEntity | null {
        if (!routerParams.pageId && !isUuid(routerParams.pageId)) {
            return null;
        }

        const currentPage = pages.find((page: SitePageEntity) => page.id.toString() === routerParams.pageId);

        return currentPage || null;
    }

    public mapPageToEntityPage(page: SitePage): SitePageEntity {
        return { ...page, is_action_pending: false };
    }

    public mapPagesToEntityPages(pages: SitePage[]): SitePageEntity[] {
        return pages.map((page: SitePage) => this.mapPageToEntityPage(page));
    }

    public isPageContentSourceWebsite(contentSourceId: EContentSources): boolean {
        return [
            EContentSources.Website,
            EContentSources.WebChatBot,
            EContentSources.WebEducation,
            EContentSources.Education,
        ].includes(contentSourceId);
    }

    public isPageContentSourceMiniLanding(contentSourceId: EContentSources): boolean {
        return [EContentSources.MiniLanding, EContentSources.ChatBot, EContentSources.PopUp].includes(contentSourceId);
    }
}
